import { Theme } from "@mui/material/styles";

import { MENU_ITEM_IMAGE_SPACING } from "~/components/global-navigation/components/menu-item/constants";
import { FILTER_DESKTOP_WIDTH } from "~/components/products-layout/components/products-filters/constants";
import { CONTAINER_MAX_WIDTH } from "~/theme/container";

/**
 * DS `ProductCarousel` slide geometry (`@oscar/common-ui` ProductCarousel `constants.ts` /
 * `styled.ts`, not exported by the package): a fixed-width card on mobile, then a number of
 * cards per view.
 */
const PRODUCT_CAROUSEL = {
  mobileCardWidth: 142,
  tabletSlidesPerView: 3.5,
  desktopSlidesPerView: 5.4,
};

/** DS `ArticleCard` horizontal-large image width (`HORIZONTAL_IMAGE_SIZE_LARGE`, not exported). */
const ARTICLE_CARD_FULL_WIDTH_IMAGE = 630;
const COLLECTION_CAROUSEL_CARD_VW = { mobile: 45, tablet: 25, desktop: 17 };

export interface ImageSizes {
  productGrid: string;
  ltcGrid: string;
  articleCard: string;
  articleCardFullWidth: string;
  productCarousel: string;
  collectionCarousel: string;
  navTile: string;
}

const px = (value: string | number): number => Number.parseFloat(String(value));
const minWidth = (breakpoint: number, size: string): string =>
  `(min-width: ${breakpoint}px) ${size}`;
const share = (columns: number): string => `${Math.round(1000 / columns) / 10}vw`;

/**
 * Rendered image width, as a `sizes` attribute
 */
export const getImageSizes = (theme: Theme): ImageSizes => {
  const { sm, md, lg, xl } = theme.breakpoints.values;
  const space = (units: number): number => px(theme.spacing(units));

  const padding = { xs: space(2), sm: space(4), lg: space(8) };
  const containerContent = CONTAINER_MAX_WIDTH - 2 * padding.lg;

  const gutter = space(0.25);
  const sidebar = FILTER_DESKTOP_WIDTH + space(3);
  const gridCard = (columns: number, reserved: number): string =>
    `calc(${share(columns)} - ${Math.round((reserved + (columns - 1) * gutter) / columns)}px)`;
  const gridCardAtMaxWidth = (columns: number, reserved: number): string =>
    `${Math.round((containerContent - reserved - (columns - 1) * gutter) / columns)}px`;

  const cardMargin = 2 * space(1.5);
  const articleCard = (columns: number, containerPadding: number): string =>
    `calc(${share(columns)} - ${Math.round(
      (2 * containerPadding - cardMargin) / columns + cardMargin,
    )}px)`;

  const carouselCard = (slidesPerView: number): string =>
    `${Math.floor(100 / slidesPerView) - 1}vw`;

  return {
    productGrid: [
      minWidth(CONTAINER_MAX_WIDTH, gridCardAtMaxWidth(4, sidebar)),
      minWidth(lg, gridCard(4, 2 * padding.lg + sidebar)),
      minWidth(md, gridCard(3, 2 * padding.sm + sidebar)),
      minWidth(sm, "33vw"),
      "50vw",
    ].join(", "),
    ltcGrid: [
      minWidth(CONTAINER_MAX_WIDTH, gridCardAtMaxWidth(5, 0)),
      minWidth(xl, gridCard(5, 2 * padding.lg)),
      minWidth(lg, gridCard(4, 2 * padding.lg)),
      minWidth(sm, gridCard(3, 2 * padding.sm)),
      gridCard(2, 2 * padding.xs),
    ].join(", "),
    articleCard: [
      minWidth(lg, articleCard(3, padding.lg)),
      minWidth(md, articleCard(3, padding.sm)),
      minWidth(sm, articleCard(2, padding.sm)),
      articleCard(1, padding.xs),
    ].join(", "),
    articleCardFullWidth: [
      minWidth(md, `${ARTICLE_CARD_FULL_WIDTH_IMAGE}px`),
      minWidth(sm, articleCard(2, padding.sm)),
      articleCard(1, padding.xs),
    ].join(", "),
    productCarousel: [
      minWidth(md, carouselCard(PRODUCT_CAROUSEL.desktopSlidesPerView)),
      minWidth(sm, carouselCard(PRODUCT_CAROUSEL.tabletSlidesPerView)),
      `${PRODUCT_CAROUSEL.mobileCardWidth}px`,
    ].join(", "),
    collectionCarousel: [
      minWidth(md, `${COLLECTION_CAROUSEL_CARD_VW.desktop}vw`),
      minWidth(sm, `${COLLECTION_CAROUSEL_CARD_VW.tablet}vw`),
      `${COLLECTION_CAROUSEL_CARD_VW.mobile}vw`,
    ].join(", "),
    navTile: theme.spacing(MENU_ITEM_IMAGE_SPACING),
  };
};
